import React, { memo } from 'react';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiHeart, FiMessageCircle, FiShare2, FiFlag } from 'react-icons/fi';
import PostMedia from './PostMedia';

const formatCount = (n) => {
    if (!n || n === 0) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
};

const SmallMuteIndicator = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
);

const PostCard = memo(({
    post,
    videoStats,
    likedVideos,
    registerPlayer,
    onSoundStatus,
    isActive,
    shouldMountPlayer,
    soundPref,
    soundStatus,
    onLike,
    onShare,
    onComment,
    onReport,
    onPlay,
    onTogglePlayPause,
    onToggleMute,
    onEnableSound,
    onMediaTap,
    isPlaying,
}) => {
    const content = post.description || post.caption || post.text;
    const username = post.username || post.author || 'username';
    const stats = videoStats[post.id] || {};
    const isLiked = likedVideos.has(post.id);

    // The icon reflects what the player reports, not just the preference.
    const soundOn = soundPref === 'on';
    const blocked = !!soundStatus?.blocked;
    const audible = soundOn && !blocked && soundStatus?.muted !== true;
    const showSoundPrompt = isActive && (soundPref === null || (soundOn && blocked));

    return (
        <div className="reel-post">
            <div className="post-media" data-post-id={post.id}>
                <PostMedia
                    post={post}
                    onPlay={() => onPlay(post)}
                    registerPlayer={registerPlayer}
                    onSoundStatus={onSoundStatus}
                    shouldMountPlayer={shouldMountPlayer}
                />
                {/* Parent-owned layer over the player: taps and swipes land in our
                    document, so they count as user gestures we can relay. */}
                <div
                    className="gesture-layer"
                    data-gesture-layer
                    onClick={(e) => { e.preventDefault(); if (onMediaTap) onMediaTap(); }}
                />
                {showSoundPrompt && (
                    <button
                        className="sound-prompt"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onEnableSound) onEnableSound(); }}
                    >
                        <FiVolume2 />
                        <span>Tap for sound</span>
                    </button>
                )}
                {isActive && !audible && !showSoundPrompt && (
                    <div className="mute-indicator" aria-label="Video is muted">
                        <SmallMuteIndicator />
                    </div>
                )}
            </div>

            <div className="reel-info">
                <span className="reel-username">@{username}</span>
                <span className="reel-platform">{post.source}</span>
                {content && (
                    <span className="reel-description" onClick={() => onComment(post.id)}>
                        {content}
                    </span>
                )}
            </div>

            <div className="reel-actions">
                <button
                    className="reel-action-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onTogglePlayPause) onTogglePlayPause(post.id, !isPlaying); }}
                >
                    {isPlaying ? <FiPause /> : <FiPlay />}
                </button>

                <button
                    className="reel-action-btn"
                    aria-label={audible ? 'Mute' : 'Unmute'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onToggleMute) onToggleMute(post); }}
                >
                    {audible ? <FiVolume2 /> : <FiVolumeX />}
                </button>

                <button
                    className={`reel-action-btn ${isLiked ? 'liked' : ''}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike(post); }}
                >
                    <FiHeart />
                    <span className="reel-action-count">{formatCount(stats.total_likes)}</span>
                </button>

                <button
                    className="reel-action-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onComment(post.id); }}
                >
                    <FiMessageCircle />
                    <span className="reel-action-count">{formatCount(stats.total_comments)}</span>
                </button>

                <button
                    className="reel-action-btn"
                    onClick={() => onShare(post)}
                >
                    <FiShare2 />
                    <span className="reel-action-count">{formatCount(stats.total_shares)}</span>
                </button>

                <button
                    className="reel-action-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReport(post); }}
                >
                    <FiFlag />
                </button>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.post.id === nextProps.post.id &&
        prevProps.post.source === nextProps.post.source &&
        prevProps.likedVideos === nextProps.likedVideos &&
        prevProps.videoStats === nextProps.videoStats &&
        prevProps.isActive === nextProps.isActive &&
        prevProps.shouldMountPlayer === nextProps.shouldMountPlayer &&
        prevProps.soundPref === nextProps.soundPref &&
        prevProps.soundStatus === nextProps.soundStatus &&
        prevProps.isPlaying === nextProps.isPlaying
    );
});

PostCard.displayName = 'PostCard';

export default PostCard;
